import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, CardMedia, Chip, Stack,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, Select, MenuItem, FormControl, InputLabel, Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../api';
import { useLanguage } from '../i18n/LanguageContext';
import { API_CONFIG } from '../config';

interface Meal {
  id: string;
  timestamp: string;
  photo_url?: string;
  description: string;
  estimated_calories: number;
  meal_type: string;
}

interface MealStats {
  total_calories: number;
  average_calories_per_day: number;
  meals_by_type: Record<string, number>;
}

const MEAL_COLORS: Record<string, string> = {
  breakfast: '#FF9800',
  lunch: '#4CAF50',
  dinner: '#2196F3',
  snack: '#9C27B0',
};

interface MealHistoryProps {
  refreshCount?: number;
}

const MealHistory: React.FC<MealHistoryProps> = ({ refreshCount }) => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [stats, setStats] = useState<MealStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const { t, locale } = useLanguage();

  const [fetchError, setFetchError] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editMeal, setEditMeal] = useState<Meal | null>(null);
  const [editDesc, setEditDesc] = useState('');
  const [editCals, setEditCals] = useState('');
  const [editType, setEditType] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const fetchMeals = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      const startStr = `${start.toISOString().slice(0, 10)}T00:00:00Z`;
      const endStr = now.toISOString();
      const response = await api.get(`/meals?start_date=${encodeURIComponent(startStr)}&end_date=${encodeURIComponent(endStr)}`);
      setMeals(response.data);
    } catch (err) {
      console.error('Error fetching meals:', err);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await api.get('/meals/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeals();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCount]);

  const handleDelete = async () => {
    if (!deleteId) return;
    const idToDelete = deleteId;
    setDeleting(true);
    setDeleteId(null); // close dialog immediately
    setMeals(prev => prev.filter(m => m.id !== idToDelete)); // optimistic remove
    try {
      await api.delete(API_CONFIG.endpoints.meal(idToDelete));
      setSuccess(t.mealDeleted);
      fetchStats(); // list already updated; only stats need refresh
    } catch {
      setError(t.mealDeleteFailed);
      fetchMeals(); // restore from server on error
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (meal: Meal) => {
    setEditMeal(meal);
    setEditDesc(meal.description);
    setEditCals(meal.estimated_calories?.toString() || '');
    setEditType(meal.meal_type);
  };

  const handleEditSave = async () => {
    if (!editMeal) return;
    const meal = editMeal;
    const desc = editDesc;
    const cals = editCals;
    const type = editType;
    setEditSaving(true);
    setEditMeal(null); // close dialog immediately
    // Optimistic update in list
    setMeals(prev => prev.map(m => m.id === meal.id
      ? { ...m, description: desc, estimated_calories: parseFloat(cals) || m.estimated_calories, meal_type: type }
      : m
    ));
    try {
      await api.put(API_CONFIG.endpoints.meal(meal.id), {
        description: desc,
        estimated_calories: parseFloat(cals) || undefined,
        meal_type: type,
      });
      setSuccess(t.mealUpdated);
      fetchStats(); // list already updated; only stats need refresh
    } catch {
      setError(t.mealUpdateFailed);
      fetchMeals(); // restore from server on error
    } finally {
      setEditSaving(false);
    }
  };

  const chartData = React.useMemo(() => {
    const byDay: Record<string, Record<string, number>> = {};
    meals.forEach(meal => {
      const iso = meal.timestamp.slice(0, 10);
      if (!byDay[iso]) byDay[iso] = {};
      const mt = meal.meal_type || 'other';
      byDay[iso][mt] = (byDay[iso][mt] || 0) + (meal.estimated_calories || 0);
    });
    return Object.entries(byDay)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-7)
      .map(([iso, types]) => ({
        date: new Date(iso + 'T12:00:00').toLocaleDateString(locale, { weekday: 'short', day: 'numeric' }),
        ...types,
      }));
  }, [meals, locale]);

  const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t.total7d, value: stats ? Math.round(stats.total_calories) : null },
          { label: t.avgDay,  value: stats ? Math.round(stats.average_calories_per_day) : null },
        ].map(({ label, value }) => (
          <Grid key={label} size={{ xs: 6 }}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                {statsLoading || !stats ? (
                  <>
                    <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
                    <Skeleton variant="text" width="40%" height={44} sx={{ mx: 'auto' }} />
                    <Skeleton variant="text" width="30%" sx={{ mx: 'auto' }} />
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="h5" fontWeight="bold">{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.kcal}</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {chartData.length > 0 && (
        <Card sx={{ borderRadius: 4, mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>{t.dailyCalories}</Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                {mealTypes.map(mt => (
                  <Bar key={mt} dataKey={mt} stackId="calories" fill={MEAL_COLORS[mt]} name={mt} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Typography variant="subtitle2" gutterBottom>{t.recentMeals}</Typography>
      {loading ? (
        <Stack spacing={1.5}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={80} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      ) : fetchError ? (
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchMeals}>{t.retry}</Button>
        }>
          {t.couldNotLoadMeals}
        </Alert>
      ) : (
        <>
          {(() => {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
            const cutoff = sevenDaysAgo.toISOString().slice(0, 10);
            const visible = meals
              .filter(m => m.timestamp.slice(0, 10) >= cutoff)
              .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
            return visible.length > 0 ? (
              <Stack spacing={1.5}>
                {visible.map((meal) => (
                  <Card key={meal.id} sx={{ borderRadius: 3 }}>
                    <Box sx={{ display: 'flex' }}>
                      {meal.photo_url && (
                        <CardMedia
                          component="img"
                          sx={{ width: 80, height: 80 }}
                          image={meal.photo_url}
                          alt={meal.description}
                        />
                      )}
                      <CardContent sx={{ py: 1.5, flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {meal.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={`${meal.estimated_calories} ${t.kcal}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <IconButton size="small" onClick={() => openEdit(meal)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => setDeleteId(meal.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <Chip
                            label={meal.meal_type}
                            size="small"
                            sx={{ bgcolor: MEAL_COLORS[meal.meal_type] || '#999', color: 'white', fontSize: 10 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(meal.timestamp).toLocaleString(locale)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Box>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                {t.noMealsYet}
              </Typography>
            );
          })()}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>{t.confirmDeleteMeal}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>{t.cancel}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t.delete}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editMeal} onClose={() => setEditMeal(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t.edit}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t.describeFood}
              value={editDesc}
              onChange={e => setEditDesc(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
            <TextField
              label={t.estimatedCalories}
              value={editCals}
              onChange={e => setEditCals(e.target.value)}
              type="number"
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t.mealType}</InputLabel>
              <Select
                value={editType}
                label={t.mealType}
                onChange={e => setEditType(e.target.value)}
              >
                {mealTypes.map(mt => (
                  <MenuItem key={mt} value={mt}>{t[mt as keyof typeof t] as string || mt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMeal(null)}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? t.saving : t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MealHistory;
